import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listTenantUsers, deactivateUser, activateUser } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { buildTenantUserListBody, buildDeactivateUsersBody, buildActivateUsersBody } from '../../constants';
import { mapTenantUserListRowToUi, normalizePagedMeta } from '../../utils';

const DEFAULT_USER_META = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: 20,
  has_next: false,
  has_previous: false,
};

/**
 * Tenant users list (POST /v1/user/list) + deactivate / activate.
 */
export function useWorkspaceTenantUsers({
  workspaceId,
  contextLoading,
  page,
  setPage,
  pageSize,
  debouncedSearchKeyword,
}) {
  const { t } = useTranslation('workspaces');
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(DEFAULT_USER_META);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const prevTenantUuidRef = useRef(workspaceId);
  const prevDebouncedSearchRef = useRef(null);

  useEffect(() => {
    if (prevDebouncedSearchRef.current === null) {
      prevDebouncedSearchRef.current = debouncedSearchKeyword;
      return;
    }
    if (prevDebouncedSearchRef.current !== debouncedSearchKeyword) {
      prevDebouncedSearchRef.current = debouncedSearchKeyword;
      startTransition(() => setPage(1));
    }
  }, [debouncedSearchKeyword, setPage]);

  useEffect(() => {
    if (!workspaceId || contextLoading) return;

    const tenantChanged = prevTenantUuidRef.current !== workspaceId;
    prevTenantUuidRef.current = workspaceId;
    if (tenantChanged && page !== 1) {
      startTransition(() => setPage(1));
      return;
    }

    let cancelled = false;
    startTransition(() => {
      setListLoading(true);
      setListError(null);
    });

    listTenantUsers(
      buildTenantUserListBody(workspaceId, page, pageSize, debouncedSearchKeyword),
    )
      .then((res) => {
        if (cancelled) return;
        const normalized = validateApiResponse(res, t('detail.users.listError'));
        const rows = Array.isArray(normalized.body) ? normalized.body : [];
        setUsers(rows.map(mapTenantUserListRowToUi));
        setMeta(normalizePagedMeta(normalized.meta, page, pageSize));
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err?.message ?? t('common.requestFailed'));
        setUsers([]);
        setMeta({ ...DEFAULT_USER_META, current_page: page, per_page: pageSize });
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, page, pageSize, contextLoading, debouncedSearchKeyword, t, setPage]);

  const refetchUsers = useCallback(() => {
    if (!workspaceId) return Promise.resolve();
    return listTenantUsers(
      buildTenantUserListBody(workspaceId, page, pageSize, debouncedSearchKeyword),
    ).then(
      (res) => {
        const normalized = validateApiResponse(res, t('detail.users.listError'));
        const rows = Array.isArray(normalized.body) ? normalized.body : [];
        setUsers(rows.map(mapTenantUserListRowToUi));
        setMeta(normalizePagedMeta(normalized.meta, page, pageSize));
      },
    );
  }, [workspaceId, page, pageSize, debouncedSearchKeyword, t]);

  const deactivateUsers = useCallback(
    async (tenantId, userIds) => {
      const res = await deactivateUser(buildDeactivateUsersBody(tenantId, userIds));
      validateApiResponse(res, t('detail.users.deactivateFailed'));
    },
    [t],
  );

  const activateUsers = useCallback(
    async (tenantId, userIds) => {
      const res = await activateUser(buildActivateUsersBody(tenantId, userIds));
      validateApiResponse(res, t('detail.users.activateFailed'));
    },
    [t],
  );

  return useMemo(
    () => ({
      users,
      meta,
      listLoading,
      listError,
      refetchUsers,
      deactivateUsers,
      activateUsers,
    }),
    [users, meta, listLoading, listError, refetchUsers, deactivateUsers, activateUsers],
  );
}
